<template>
  <div class="container">
    <o-bread-crumb url="/feature-flags">
      <template v-slot:pageName>Feature Flag</template>
      <template v-slot:pageDetail>Audit Log</template>
      <template v-slot:pageDescription><br /></template>
    </o-bread-crumb>
    
    <div class="card text-center">
      <div class="card-header">
        <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
          <li class="nav-item">
            <div class="d-flex d-inline-block">
              <a class="nav-link text-dark fs-4" href="#">Feature Flag Audit Logs Tenant {{ namaCorporate}}</a>
            </div>
          </li>
        </ul>
      </div>
      
      <div class="card-body">
        <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
        <div class="spinner" v-if="page.isLoading"></div>
        
        <div v-if="page.output">
          <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
            <li class="nav-item">           
              <div class="d-flex d-inline-block">
                <o-search :searchOptions="searchOptions" @submit="search" @clear="clearSearch"></o-search>
              </div>
            </li>
            <li class="nav-item btn btn-primary btn-sm">
              <button class="reload nav-link text-white" @click.prevent="load_state()">&#x21bb;</button>
            </li>
          </ul>
          
          <div class="table-responsive">
            <o-table class="table table-hover">
              <thead>
                <tr v-if="fields">
                 <th class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                    {{ field }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in featureFlagAuditLogs" :key="index">
                  <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                  <td>{{ item.feature }}</td>
                  <td><span class="badge" :class="getBadgeClass(item.old_flag)">{{ item.old_flag }}</span></td>
                  <td><span class="badge" :class="getBadgeClass(item.new_flag)">{{ item.new_flag }}</span></td>
                  <td class="json-viewer">
                    <pre v-if="isExpanded.oldMetadata[index]" @click="toggle(index, 'oldMetadata')">{{ formatJson(item.old_metadata) }}</pre>
                    <span v-else @click="toggle(index, 'oldMetadata')">{{ collapsedJson(item.old_metadata) }}</span>
                    <button @click="toggle(index, 'oldMetadata')" class="btn btn-link custom-btn">{{ isExpanded.oldMetadata[index] ? 'Hide' : 'Show' }}</button>
                  </td>
                  <td class="json-viewer">
                    <pre v-if="isExpanded.newMetadata[index]" @click="toggle(index, 'newMetadata')">{{ formatJson(item.new_metadata) }}</pre>
                    <span v-else @click="toggle(index, 'newMetadata')">{{ collapsedJson(item.new_metadata) }}</span>
                    <button @click="toggle(index, 'newMetadata')" class="btn btn-link custom-btn">{{ isExpanded.newMetadata[index] ? 'Hide' : 'Show' }}</button>
                  </td>
                  <td>{{ item.changed_by_name }}</td>
                  <td>{{ formatDate(item.changed_at) }}</td>
                </tr>
              </tbody>
            </o-table>
          </div>
        </div>
      </div>
      
      <div class="container mt-5">
        <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
      </div>
    </div>
  </div>
</template>

<script>
import { OBreadCrumb, OTable, OSearch, OPagination } from '@/components/OmnihControl';
import { computed, onMounted, reactive, ref, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import store from '@/store/';

export default {
  setup() {
    const route = useRoute();
    let lastActivePage = 1;

    const page = reactive({
      isLoading: false,
      output: false
    });

    const isExpanded = reactive({
      oldMetadata: [],
      newMetadata: []
    });

    const showCorporateId = reactive([]);

    let fields = ref([
      'No',
      'Feature',
      'Old Flag',
      'New Flag',
      'Old Metadata',
      'New Metadata',
      'Changed By',
      'Changed At'
    ]);

    const searchOptions = reactive([
      { value: 'feature', text: 'feature' },
      { value: 'changed_by_name', text: 'changed by' }
    ]);

    let meta = computed(() => store.state.FeatureFlagAuditLog.meta);
    const featureFlagAuditLogs = computed(() => store.state.FeatureFlagAuditLog.featureFlagAuditLogs);

    onMounted(() => {
      store.commit('FeatureFlagAuditLog/reset_meta');
      load_state();
    });

    onBeforeUnmount(() => {
      const updatedMeta = { ...meta.value, page: lastActivePage };
      store.commit('FeatureFlagAuditLog/switch_page', { meta: updatedMeta });

      store.commit('FeatureFlagAuditLog/clear_search');
      store.dispatch('FeatureFlagAuditLog/read').then(() => {});
    });

    const namaCorporate = ref('');

    function load_state() {
      page.isLoading = true;
      const corporateId = route.params.corporateId;

      if (corporateId === 'all') {
        namaCorporate.value = 'All Corporates';
      } else {
        store.dispatch('Corporate/read_single', corporateId)
          .then(data => {
            namaCorporate.value = data.name;
          })
          .catch(() => {
            namaCorporate.value = 'Corporate';
          });
      }

      store.dispatch('FeatureFlagAuditLog/read', corporateId)
        .catch(err => console.error('Error loading audit logs:', err))
        .finally(() => {
          page.isLoading = false;
          page.output = true;
        });
    }

    function navPage(pageNumber) {
      const updatedMeta = { ...meta.value, page: pageNumber };

      store.commit('FeatureFlagAuditLog/switch_page', { meta: updatedMeta });
      load_state();
    }

    function search(searchBy) {
      const { selected, criteria } = searchBy;

      if (selected && criteria) {
        let search = {};
        search[selected] = criteria;

        const updatedMeta = { ...meta.value, page: 1 };
        store.commit('FeatureFlagAuditLog/switch_page', { meta: updatedMeta });

        store.commit('FeatureFlagAuditLog/set_search', { search });
        const corporateId = route.params.corporateId;
        store.dispatch('FeatureFlagAuditLog/read', corporateId).then(() => {});
      }
    }

    function clearSearch() {
      const updatedMeta = { ...meta.value, page: lastActivePage };
      store.commit('FeatureFlagAuditLog/switch_page', { meta: updatedMeta });

      store.commit('FeatureFlagAuditLog/clear_search');
      const corporateId = route.params.corporateId;
      store.dispatch('FeatureFlagAuditLog/read', corporateId).then(() => {});
    }

    function formatJson(jsonData) {
      if (!jsonData) return '';
      if (typeof jsonData === 'object') {
        return JSON.stringify(jsonData, null, 2);
      }
      try {
        const parsed = JSON.parse(jsonData);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return jsonData;
      }
    }

    function collapsedJson(jsonData) {
      if (!jsonData) return '';
      if (typeof jsonData === 'object') {
        return JSON.stringify(jsonData, null, 2).slice(0, 50) + '...';
      }
      try {
        const parsed = JSON.parse(jsonData);
        return JSON.stringify(parsed, null, 2).slice(0, 50) + '...';
      } catch {
        return String(jsonData).slice(0, 50) + '...';
      }
    }

    function toggle(index, type) {
      isExpanded[type][index] = !isExpanded[type][index];
    }

    function formatDate(date) {
      const newDate = new Date(date);

      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      };

      const datePart = newDate.toLocaleDateString('en-US', options).split(', ');

      const formattedDate = `${datePart[0]} on ${datePart[1]} ${datePart[2]}`;

      return formattedDate;
    }

    function shortenId(id) {
      if (!id) return '';
      return id.slice(0, 8) + '...';
    }

    function toggleCorporateId(index) {
      showCorporateId[index] = !showCorporateId[index];
    }

    function getBadgeClass(flag) {
      switch (flag) {
        case 'NONACTIVE':
          return 'bg-danger';
        case 'CENTRALIZE':
          return 'bg-primary';
        case 'DECENTRALIZE':
          return 'bg-success';
        default:
          return 'bg-secondary';
      }
    }

    return {
      page,
      searchOptions,
      meta,
      fields,
      featureFlagAuditLogs,
      load_state,
      navPage,
      search,
      clearSearch,
      formatJson,
      formatDate,
      toggle,
      isExpanded,
      collapsedJson,
      shortenId,
      showCorporateId,
      toggleCorporateId,
      namaCorporate,
      getBadgeClass
    };
  },
  components: {
    OTable,
    OPagination,
    OBreadCrumb,
    OSearch,
  }

};
</script>

<style scoped>
.container {
  overflow-x: auto;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.table {
  width: 100%;
  table-layout: fixed;
}

.table th,
.table td {
  word-wrap: break-word;
  white-space: normal;
}

.json-viewer pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: pointer;
  color: black;
  text-align: left;
  max-height: 300px;
  overflow-y: auto;
}

.json-viewer span {
  cursor: pointer;
  color: black;
}

.json-viewer button {
  margin-left: 10px;
  cursor: pointer;
  background: none;
  border: none;
}

.custom-btn {
  text-decoration: none;
  padding: 0;
  color: #cb5b5b;
}

.btn-link:hover {
  color: black;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
  border-radius: 6px !important;
}
</style>
